/* istanbul ignore file */
import { FC, useState } from "react";
import { Redirect, Route } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import {
  DataDescriptorEntity,
  EnterpriseAppState,
  FarmEntity,
  GamePlanFormValues,
  GamePlans,
  Breadcrumbs
} from "@ctra/api";

import { Enterprise as Content, useTranslation } from "@ctra/i18n";
import { CompassOutlined, CtraLayout } from "@ctra/components";
import { GAContext } from "@ctra/analytics";
import { isPending, Nullable, useDidMount } from "@ctra/utils";

import { Wizard, WizardDomain, WizardIntent, useConversion, GACategories } from "@game-plans";
import { useLocalization } from "@base";
import { Routes } from "@routes";

const { WidgetWrapper } = CtraLayout;

type CreateGamePlanProps = {
  farmID?: Nullable<FarmEntity["id"]>;
  dataDescriptorID?: Nullable<DataDescriptorEntity["id"]>;
};

/**
 * Wrapper component for creating a game plan
 * @constructor
 */
export const CreateGamePlan: FC<CreateGamePlanProps> = ({ farmID = null, dataDescriptorID = null }) => {
  const routes = Routes.app.gamePlans.create;

  const [mayRedirect, setMayRedirect] = useState<boolean>(false);
  const [fromFormValues] = useConversion();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isMounting] = useDidMount();
  const { unitSystem } = useLocalization();

  const spinning = useSelector<EnterpriseAppState, boolean>(
    (state) =>
      isPending(state, GamePlans.types.CREATE_GAME_PLAN) || isPending(state, GamePlans.types.CREATE_TODO_LIST)
  );

  const created = mayRedirect && !isMounting && !spinning;

  if (dataDescriptorID && !farmID) {
    console.warn("Data descriptor ID without farm ID: discarding it and using NULL.");
  }

  const initialValues: GamePlanFormValues = {
    farmID,
    dataDescriptorID: farmID ? dataDescriptorID : null,
    hasTarget: 1,
    title: "",
    description: "",
    timePeriod: [null, null],
    taskList: [],
    unitSystem
  };

  return (
    <WidgetWrapper icon={<CompassOutlined />} title={t<string>(Content.gamePlan.wizard.title.create)}>
      <GAContext.Provider value={{ category: GACategories.gamePlanCreator }}>
        <Breadcrumbs.components.Breadcrumb title={t<string>(Content.gamePlan.wizard.title.create)} />
        {/* Redirect from create index to the first step */}
        <Route exact path={routes.index}>
          {({ location: { pathname, search } }) => <Redirect to={{ pathname: routes.general, search }} />}
        </Route>
        {/* Redirect after successfully creating a game plan */}
        {created && <Redirect to={Routes.app.gamePlans.index} />}
        <Wizard
          spinning={spinning}
          paths={routes}
          initialValues={initialValues}
          onSubmit={(values, { setSubmitting }) => {
            dispatch(GamePlans.actions.createGamePlan.start(fromFormValues(values)));
            setMayRedirect(true);
            setSubmitting(false);
          }}
          domain={WizardDomain.Global}
          intent={WizardIntent.Create}
        />
      </GAContext.Provider>
    </WidgetWrapper>
  );
};
